import { all, call, fork, put, takeLatest } from 'redux-saga/effects';
import i18n from 'i18next';

import {
  CHANGE_VALVE_STATE, DELETE_DEVICE, UPDATE_DEVICE, SET_DEVICE_SYSTEM_MODE, RUN_HEALTH_TEST, FETCH_DEVELOPER_MENU_SETTINGS, UPDATE_DEVELOPER_MENU_SETTINGS
} from '../constants/ActionTypes';

import { showError, showMessage } from '../actions/Common';
import { CustomAction } from 'model/state';
import { changeValveStateSuccess, changeValveStateError, DevicePayload, setDeviceSystemModeSuccess, setDeviceSystemModeError, deleteDeviceError, deleteDeviceSuccess, updateDeviceSuccess, updateDeviceError, fetchDeveloperMenuSettingsSuccess, fetchDeveloperMenuSettingsError, DeveloperMenuPayload } from 'actions';
import { deleteDevice, saveDevice, setDeviceSystemMode, updateDevice, runHealthTest, getDevice, updateDeveloperMenuSettings } from 'api/Device';
import { fromValveState, fromDeviceSystemMode, fromPartialDevice, toDeveloperMenuSettings } from 'model/api';
import { MessageType } from '../model';

function* changeValveStateWorker(action: CustomAction<DevicePayload>) {
  const deviceId = action.payload?.deviceId;
  const target = action.payload?.target;
  try {
    if (target === undefined || deviceId === undefined) {
      throw Error('Invalid Parameters');
    }
    yield call(saveDevice, deviceId, { valve: { target: fromValveState(target) } });

    yield put(changeValveStateSuccess());
  } catch (err) {
    yield put(showError(i18n.t('connection_error'), err));
    yield put(changeValveStateError());
  }
}

function* setSystemModeWorker(action: CustomAction<DevicePayload>) {
  const deviceId = action.payload?.deviceId;
  const systemMode = action.payload?.systemMode;
  try {
    if (systemMode === undefined || deviceId === undefined) {
      throw Error('Invalid Parameters');
    }
    yield call(setDeviceSystemMode, deviceId, fromDeviceSystemMode(systemMode));

    yield put(setDeviceSystemModeSuccess());
  } catch (err) {
    yield put(showError(i18n.t('connection_error'), err));
    yield put(setDeviceSystemModeError());
  }
}

function* deleteDeviceWorker(action: CustomAction<DevicePayload>) {
  const deviceId = action.payload?.deviceId;
  try {
    if (deviceId === undefined) {
      throw Error('Invalid Parameters');
    }

    yield call(deleteDevice, deviceId);
    yield put(deleteDeviceSuccess());
  } catch (err) {
    yield put(showError(i18n.t('connection_error'), err));
    yield put(deleteDeviceError());
  }
}

function* updateDeviceWorker(action: CustomAction<DevicePayload>) {
  const deviceId = action.payload?.deviceId;
  const device = action.payload?.device;

  try {
    if (deviceId === undefined || device === undefined) {
      throw Error('Invalid Parameters');
    }
    yield call(updateDevice, deviceId, fromPartialDevice(device));
    yield put(updateDeviceSuccess());
  } catch (err) {
    yield put(showError(i18n.t('connection_error'), err));
    yield put(updateDeviceError());
  }
}

function* updateDeveloperMenuSettingsWorker(action: CustomAction<DeveloperMenuPayload>) {
  const deviceId = action.payload?.deviceId;
  const settings = action.payload?.settings;
  try {
    if (deviceId === undefined || settings === undefined) {
      throw Error('Invalid Parameters');
    }
    yield call(updateDeveloperMenuSettings, deviceId, settings);
    
    yield put(showMessage({ message: i18n.t('thresholds-saved-title'), description: i18n.t('thresholds-saved-description'), type: MessageType.SUCCESS }));
    yield put(updateDeviceSuccess());
  } catch (err) {
    yield put(showError(i18n.t('connection_error'), err));
    yield put(updateDeviceError());
  }
}

function* fetchDeveloperMenuSettingsWorker(action: CustomAction<DevicePayload>) {
  const deviceId = action.payload?.deviceId;

  try {
    if (deviceId === undefined) {
      throw Error('Invalid Parameters');
    }
    const device = yield call(getDevice, deviceId);
    
    yield put(fetchDeveloperMenuSettingsSuccess(toDeveloperMenuSettings(device)));
  } catch (err) {
    yield put(showError(i18n.t('connection_error'), err));
    yield put(fetchDeveloperMenuSettingsError());
  }
}

function* runHealthTestWorker(action: CustomAction<DevicePayload>) {
  const deviceId = action.payload?.deviceId;

  try {
    if (deviceId === undefined) {
      throw Error('Invalid Parameters');
    }

    yield call(runHealthTest, deviceId);
    yield put(showMessage({ message: i18n.t('run-health-test-success-title'), description: i18n.t('run-health-test-success-desc'), type: MessageType.SUCCESS }));
  } catch (err) {
    yield put(showError(i18n.t('connection_error'), err));
  }
}

export function* changeValveStateSaga() {
  yield takeLatest(CHANGE_VALVE_STATE, changeValveStateWorker);
}

export function* setSystemModeSaga() {
  yield takeLatest(SET_DEVICE_SYSTEM_MODE, setSystemModeWorker);
}

export function* deleteDeviceSaga() {
  yield takeLatest(DELETE_DEVICE, deleteDeviceWorker);
}

export function* updateDeviceSaga() {
  yield takeLatest(UPDATE_DEVICE, updateDeviceWorker);
}

export function* updateDeveloperMenuSettingsSaga() {
  yield takeLatest(UPDATE_DEVELOPER_MENU_SETTINGS, updateDeveloperMenuSettingsWorker);
}

export function* runHealthTestSaga() {
  yield takeLatest(RUN_HEALTH_TEST, runHealthTestWorker)
}

export function* fetchDeveloperMenuSettingsSaga(){
  yield takeLatest(FETCH_DEVELOPER_MENU_SETTINGS, fetchDeveloperMenuSettingsWorker)
}

export default function* deviceSagas() {
  yield all([
    fork(changeValveStateSaga),
    fork(setSystemModeSaga),
    fork(deleteDeviceSaga),
    fork(updateDeviceSaga),
    fork(runHealthTestSaga),
    fork(updateDeveloperMenuSettingsSaga),
    fork(fetchDeveloperMenuSettingsSaga)
  ]);
}
